import {Color} from '@benchling/nucleus-ui';
import {css, CSSProperties, StyleSheet} from 'aphrodite';
import React from 'react';
import {useSelector} from 'react-redux';

import {assert, assertAndContinue} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {STATUS} from 'constants/ExportConstants';
import {
  ReviewerStatus,
  ReviewLifecycleType,
  ReviewStatus,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import type {ReviewLifecycleActionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {formatListWithBoldedNames} from 'notebook/reviews/review-lifecycles/ReviewLifecyclesUtil';
import {ReviewBadgeContext, ReviewBadgeContextValue} from 'notebook/reviews/ReviewBadgeContext';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import {getEntryOrFileIdKey} from 'notebook/util/analytics/FileAnalyticsUtils';
import DocumentEditorLockUtil from 'realtime/util/DocumentEditorLockUtil';
import {BenchlingState} from 'redux-stores/reduceApp';
import EntityStore from 'stores/EntityStore';
import {
  FONT_PROXIMA_12,
  FONT_PROXIMA_14,
  FONT_PROXIMA_18,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, WHITE} from 'ui-library/Colors';
import {MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {StepObject, StepperV2} from 'ui-library/stepper/Stepper';
import APIUtil from 'util/APIUtil';

import {ReviewLifecycleSummaryPanelQuery} from './__generated/ReviewLifecycleSummaryPanelQuery';
import ReviewLifecycleSummaryItem, {SENT_FOR_REVIEW} from './review-lifecycles/ReviewLifecycleSummaryItem';
import type {ReviewLifecycleSummaryAvatar} from './review-lifecycles/ReviewLifecycleSummaryItem';
import {runSendForRLCReviewModal} from './review-lifecycles/runSendForRLCReviewModal';
import {
  findLastSentForReviewFromUserWithTimestamp,
  getHistoricalReviewStages,
  getReviewStageViewModel,
  reviewChangesIncludeSendForReview,
  type HistoricalReviewStage,
  type ReviewChangeGraphQL,
  type ReviewerGraphQL,
} from './reviewBadgeShared';
import {PermissionsGraphQL, ReviewAction, Snapshot} from './ReviewConstants';
import ReviewStatusModal, {ReviewStageInfo} from './ReviewStatusModal';
import SnapshotInfo from './SnapshotInfo';
import {LastSnapshotFailedIconAndSummary} from './SnapshotInfo';
import {useReviewSummaryPanel} from './useReviewSummaryPanel';

export {REVIEW_LIFECYCLE_SUMMARY_PANEL_QUERY} from './useReviewSummaryPanel';

type ReviewLifecycleSummaryOrInaccessibleGraphQL = ReviewLifecycleSummaryPanelQuery['reviewable'];

type ReviewableEntryGraphQL = ExtractByTypename<ReviewLifecycleSummaryOrInaccessibleGraphQL, 'EntryNode'>;

function isEntry(data?: {__typename?: string}): data is ReviewableEntryGraphQL {
  return data?.__typename === 'EntryNode';
}

function isTemplateCollectible(data?: {
  __typename?: string;
}): data is {__typename: 'TemplateNode' | 'SubtemplateNode'; id: string} {
  return data?.__typename === 'TemplateNode' || data?.__typename === 'SubtemplateNode';
}

function getTemplateIdsForReviewAnalytics(reviewable: {
  __typename?: string;
  id: string;
  template?: {id: string; templateCollection: {id: string}} | null;
}): {templateId: string | null; templateCollectionId: string | null} {
  if (isEntry(reviewable)) {
    return {
      templateId: reviewable.template?.id ?? null,
      templateCollectionId: reviewable.template?.templateCollection.id ?? null,
    };
  }
  if (isTemplateCollectible(reviewable)) {
    return {templateId: reviewable.id, templateCollectionId: null};
  }
  return {templateId: null, templateCollectionId: null};
}

export interface ReviewLifecycleSummaryPanelProps {
  permissions: PermissionsGraphQL;
}

export interface EntityByStageId {
  // TODO(BNCH-48243): Should not be nullable, reviewers don't currently support null users, even though the
  // data model allows it.
  [stageId: string]: MinimalEntity | null;
}

export interface IsTeamReassignableByStageId {
  [stageId: string]: boolean;
}

interface PrimaryReviewActionProps {
  primaryReviewAction: ReviewAction;
  userFacingMessage: string;
  sourceId: string | null;
  existingReviewerUsersByStage?: EntityByStageId;
  activeReviewLifecycleId?: string;
  templateId: string | null;
  templateCollectionId: string | null;
}

interface ReviewStagesProps {
  reviewers: Array<ReviewerGraphQL>;
  reviewableId: string;
  canReassign: boolean;
  canAccept: boolean;
  canReject: boolean;
  lifecycleType: ReviewLifecycleType;
  status: ReviewStatus;
  authors: Array<MinimalEntity> | undefined;
  reviewChanges: Array<ReviewChangeGraphQL>;
  reviewSenderId: string | undefined;
}

interface SentForReviewSummaryItemProps {
  reviewChanges: Array<ReviewChangeGraphQL>;
  reviewSenderId: string | undefined;
}

interface ReviewStageStepContent {
  stepKey: string;
  stageTitle: string | null;
  statusIconStageId: string;
  user: ReviewLifecycleSummaryAvatar;
  stageStatus: ReviewerStatus;
  completedTime: string | null;
  reviewStageAction: string;
  reviewLifecycleAction: ReviewLifecycleActionType | undefined;
  wasEsigned: boolean;
  message: string | null | undefined;
  canReassign: boolean;
  canReview: boolean;
  canAccept: boolean;
  canReject: boolean;
  isUserValidReviewer: boolean;
  hasValidReviewerReplacements?: boolean;
  stageInfo?: ReviewStageInfo;
  authors?: Array<MinimalEntity>;
}

function renderReviewStageStep(
  content: ReviewStageStepContent,
  {reviewableId, reviewSenderId}: {reviewableId: string; reviewSenderId: string | undefined}
): StepObject {
  return {
    title: (
      <div
        {...getTestProps({component: 'ReviewStageTitle', element: 'root', key: content.stepKey})}
        className={css(styles.title)}
        data-amp-safe={false}
      >
        {content.stageTitle}
      </div>
    ),
    content: (
      <div
        {...getTestProps({component: 'ReviewStage', element: 'root', key: content.stepKey})}
        className={css(styles.stepContent)}
        data-amp-safe={false}
      >
        <ReviewLifecycleSummaryItem
          canReassign={content.canReassign}
          canReview={content.canReview}
          canAccept={content.canAccept}
          canReject={content.canReject}
          user={content.user}
          reviewStageAction={content.reviewStageAction}
          isUserValidReviewer={content.isUserValidReviewer}
          hasValidReviewerReplacements={content.hasValidReviewerReplacements}
          stageInfo={content.stageInfo}
          reviewableId={reviewableId}
          completedTime={content.completedTime}
          stageStatus={content.stageStatus}
          authors={content.authors}
          wasEsigned={content.wasEsigned}
          reviewLifecycleAction={content.reviewLifecycleAction}
          message={content.message}
          reviewSenderId={reviewSenderId}
        />
      </div>
    ),
    key: content.stepKey,
    renderNumber: (
      <ReviewerStatusIcon
        reviewerStatus={content.stageStatus}
        isUserValidReviewer={content.isUserValidReviewer}
        stageId={content.statusIconStageId}
      />
    ),
  };
}

function historicalStageToStepContent(stage: HistoricalReviewStage): ReviewStageStepContent {
  return {
    stepKey: stage.key,
    stageTitle: stage.stageName,
    statusIconStageId: stage.key,
    user: {
      id: stage.creator.id,
      avatar_url: stage.creator.avatarUrl,
      email: stage.creator.__typename === 'UserNode' ? stage.creator.email : undefined,
      handle: stage.creator.handle,
      name: stage.creator.name,
    },
    stageStatus: stage.reviewStatus,
    completedTime: stage.completedTime,
    reviewStageAction: '',
    reviewLifecycleAction: stage.reviewLifecycleAction,
    wasEsigned: stage.wasEsigned,
    message: stage.message,
    canReassign: false,
    canReview: false,
    canAccept: false,
    canReject: false,
    isUserValidReviewer: true,
  };
}

export function PrimaryReviewAction({
  primaryReviewAction,
  userFacingMessage,
  existingReviewerUsersByStage,
  templateId,
  templateCollectionId,
  sourceId,
  activeReviewLifecycleId,
}: PrimaryReviewActionProps): JSX.Element {
  const {
    setIsDropdownOpen,
    reviewError,
    onReviewChange,
    performanceMetadata,
    reviewableId,
    renderAdditionalReviewInfo,
    reviewId,
    getUnsubmittedTableNames,
    documentIds,
  } = ReviewBadgeContext.useContext();
  assert(reviewableId != null, 'No ID for reviewable provided');
  assert(reviewId != null, 'No ID for review provided');
  // Check for validation errors by passing the action to the error function.
  // The action-specific logic (e.g., allowing RETRACT with unsubmitted tables) is handled
  // by the error function at the call site (e.g., EntryNoteTitleBadge.getReviewError).
  const validationError =
    reviewError?.sendForReviewValidationError?.(reviewableId, primaryReviewAction) ?? null;
  const disabled = Boolean(validationError);
  const renderedAction = (
    <div
      className={disabled ? css(styles.disabledReviewAction) : css(styles.reviewAction)}
      onClick={() => {
        if (disabled) {
          return;
        }
        setIsDropdownOpen(false);
        switch (primaryReviewAction) {
          case ReviewAction.SEND_FOR_REVIEW: {
            assertAndContinue(
              activeReviewLifecycleId != null,
              'Project RLC should exist if sending for RLC Review.'
            );
            const fileIdKey = getEntryOrFileIdKey(reviewableId);
            runSendForRLCReviewModal({
              reviewableId,
              onRequestComplete: onReviewChange,
              existingReviewerUsersByStage,
              analyticsParams: {
                project_id: sourceId,
                [fileIdKey]: reviewableId,
                reviewable_id: reviewableId,
                template_collection_id: templateCollectionId,
                template_id: templateId,
              },
              renderAdditionalReviewInfo,
              renderReviewError: reviewError?.renderReviewError,
              performanceMetadata,
              getUnsubmittedTableNames,
              documentIds,
            });
            break;
          }
          case ReviewAction.RETRACT:
            ReviewStatusModal.open({
              action: primaryReviewAction,
              onUpdate: onReviewChange,
              fileId: reviewableId,
              reviewId,
              // When canceling a review or reopening a reviewable item, reviewCriteriaGroups are not necessary.
              reviewCriteriaGroups: [],
              isForReviewLifecycle: true,
              message: ReviewUtil.getDefaultReviewComment(ReviewAction.RETRACT, true),
              renderAdditionalReviewInfo,
              renderReviewError: reviewError?.renderReviewError,
              performanceMetadata,
            });
            break;
          default:
            assertAndContinue(
              false,
              `Invalid Review Action from ReviewLifecycleSummaryPanel: ${primaryReviewAction}`
            );
            break;
        }
      }}
    >
      <div
        {...getTestProps({component: 'PrimaryReviewAction', element: 'userActionMessage'})}
        className={css(styles.reviewActionItem)}
      >
        <BenchlingIcon className={css(styles.reviewIcon)} icon={ReviewStatus.RETRACTED} />
        <div>{userFacingMessage}</div>
      </div>
    </div>
  );
  return disabled ? (
    <Tooltip text={validationError} placement={FloatingPlacement.LEFT}>
      {renderedAction}
    </Tooltip>
  ) : (
    renderedAction
  );
}

export function SentForReviewSummaryItem({
  reviewChanges,
  reviewSenderId,
}: SentForReviewSummaryItemProps): JSX.Element {
  const {reviewableId} = ReviewBadgeContext.useContext();
  assert(reviewableId != null, 'ReviewLifecycleSummaryPanel requires a reviewableId');
  const {
    author,
    timestamp: completedTime,
    wasEsigned,
    message,
  } = findLastSentForReviewFromUserWithTimestamp(reviewChanges);
  return (
    <ReviewLifecycleSummaryItem
      canReassign={false}
      canAccept={false}
      canReject={false}
      canReview={false}
      isUserValidReviewer={true}
      user={{
        id: author.id,
        avatar_url: author.avatarUrl,
        email: author.__typename === 'UserNode' ? author.email : undefined,
        handle: author.handle,
        name: author.name,
      }}
      reviewStageAction={SENT_FOR_REVIEW}
      stageStatus={ReviewerStatus.FINISHED}
      completedTime={completedTime}
      reviewableId={reviewableId}
      authors={undefined}
      wasEsigned={wasEsigned}
      message={message}
      reviewSenderId={reviewSenderId}
    />
  );
}

function getReviewStages({
  reviewers,
  canReassign,
  canAccept,
  canReject,
  lifecycleType,
  status,
  reviewableId,
  authors,
  reviewChanges,
  reviewSenderId,
}: ReviewStagesProps): Array<StepObject | null> {
  const currentUserId = EntityStore.currentUserId;
  const isSelfReview = lifecycleType === ReviewLifecycleType.SELF_REVIEW;

  return reviewers.map((reviewer) => {
    const {updatedAt, reviewStatus, reviewStage, isUserValidReviewer} = reviewer;
    const {name: stageName, action, id: stageId, description: stageDescription} = reviewStage;

    const stageViewModel = getReviewStageViewModel({
      reviewer,
      canReassign,
      lifecycleType,
      status,
      authors,
      reviewChanges,
      currentUserId: currentUserId ?? null,
    });

    if (stageViewModel == null) {
      // TODO(BNCH-42332): Handle required reassignment since reviewer does not exist for the stage
      return null;
    }

    return renderReviewStageStep(
      {
        stepKey: stageId,
        stageTitle: reviewStage.name,
        statusIconStageId: stageId,
        user: stageViewModel.avatar,
        stageStatus: reviewStatus,
        completedTime: updatedAt,
        reviewStageAction: stageViewModel.reviewStageAction,
        reviewLifecycleAction: action,
        wasEsigned: stageViewModel.wasEsigned,
        message: stageViewModel.message,
        canReassign: stageViewModel.canReassign,
        canReview: stageViewModel.canReview,
        canAccept,
        canReject,
        isUserValidReviewer,
        hasValidReviewerReplacements: stageViewModel.hasValidReviewerReplacements,
        stageInfo: {
          id: stageId,
          name: isSelfReview ? undefined : stageName,
          description: stageDescription ?? '',
        },
        authors,
      },
      {reviewableId, reviewSenderId}
    );
  });
}

function getHistoricalReviewStageSteps({
  historicalStages,
  reviewableId,
  reviewSenderId,
}: {
  historicalStages: Array<HistoricalReviewStage>;
  reviewableId: string;
  reviewSenderId: string | undefined;
}): Array<StepObject> {
  return historicalStages.map((stage) =>
    renderReviewStageStep(historicalStageToStepContent(stage), {reviewableId, reviewSenderId})
  );
}

/**
 * Inserts the snapshot section into the sole review stage step's content, for self-review lifecycles
 * only. Self-review always has exactly one stage and skips the "Sent for review" step, so this is the
 * only place its snapshot can be surfaced. Clones the step's content element to add the snapshot as a
 * direct child, matching how the "Sent for review" step renders.
 */
function withSelfReviewSnapshotSection(
  steps: Array<StepObject | null>,
  selfReviewSnapshotSection: React.ReactNode
): Array<StepObject | null> {
  const [firstStep, ...remainingSteps] = steps;
  if (firstStep == null) {
    return steps;
  }
  const stepContentElement = firstStep.content as React.ReactElement<{children: React.ReactNode}>;
  return [
    {
      ...firstStep,
      content: React.cloneElement(
        stepContentElement,
        undefined,
        stepContentElement.props.children,
        selfReviewSnapshotSection
      ),
    },
    ...remainingSteps,
  ];
}

/** @package */
export function MissingReviewersWarning({stages}: {stages: Array<string>}): JSX.Element {
  const missingReviewerStagesString = formatListWithBoldedNames(stages);
  const message = `No reviewer available for ${pluralize(
    stages.length,
    'stage'
  )}: ${missingReviewerStagesString}. Please consult your admin.`;

  return (
    <InlineBanner
      bannerType={InlineBannerType.ERROR}
      // oxlint-disable-next-line react/no-danger
      supportingInfo={<div dangerouslySetInnerHTML={{__html: message}} />}
    />
  );
}

export function ReassignStatusIcon(): JSX.Element {
  return (
    <div className={css(styles.reassignIconContainer)}>
      <BenchlingIcon icon="arrow-right" className={css(styles.reassignIcon)} />
    </div>
  );
}

function ReviewerStatusIcon({
  reviewerStatus,
  isUserValidReviewer,
  stageId,
}: {
  reviewerStatus: ReviewerStatus;
  isUserValidReviewer: boolean;
  stageId: string;
}): JSX.Element {
  let icon: JSX.Element;
  switch (reviewerStatus) {
    case ReviewerStatus.FINISHED:
      icon = <BenchlingIcon icon="done" className={css(styles.completedIcon)} />;
      break;
    case ReviewerStatus.BLOCKED:
      if (!isUserValidReviewer) {
        icon = <ReassignStatusIcon />;
      } else {
        icon = <BenchlingIcon icon="lock" className={css(styles.lockIcon)} />;
      }
      break;
    case ReviewerStatus.PENDING:
      if (!isUserValidReviewer) {
        icon = <ReassignStatusIcon />;
      } else {
        icon = <div className={css(styles.pendingIcon)} />;
      }
      break;
    case ReviewerStatus.REJECTED:
      icon = <BenchlingIcon icon="exclamation-circle" className={css(styles.rejectedIcon)} />;
      break;
    default:
      assert(false, `Received an unexpected reviewer status: ${reviewerStatus}`);
  }
  return (
    <div
      className={css(styles.iconContainer)}
      {...getTestProps({component: 'ReviewerStatusIcon', element: 'root', key: stageId})}
      data-amp-safe={false}
    >
      {icon}
    </div>
  );
}

function SnapshotSection({
  snapshot,
  reviewableId,
  reviewId,
  onReviewChange,
  canRetry,
  refetch,
}: {
  snapshot: Snapshot;
  reviewableId: string;
  reviewId: string;
  onReviewChange: ReviewBadgeContextValue['onReviewChange'];
  // Retrying regenerates a snapshot for the review's current (active-cycle) state, which would be
  // misleading while viewing a past, already-published version.
  canRetry: boolean;
  refetch: () => void;
}): JSX.Element {
  return (
    <div className={css(styles.snapshotSection)}>
      {snapshot.status === STATUS.FAILED && canRetry ? (
        <LastSnapshotFailedIconAndSummary
          fileId={reviewableId}
          reviewId={reviewId}
          onReviewChange={onReviewChange}
          refetch={refetch}
        />
      ) : (
        <SnapshotInfo
          snapshot={snapshot}
          apiType={APIUtil.apiIdToType(reviewableId)}
          showCreatedTime={false}
        />
      )}
    </div>
  );
}

/**
 * Creates a review summary panel for reviewables detailing the state for each review stage in the review lifecycle.
 * Should only be rendered for reviewables that have been sent for review at least once with a review lifecycle.
 */
export default function ReviewLifecycleSummaryPanel({
  permissions,
}: ReviewLifecycleSummaryPanelProps): JSX.Element | null {
  const {reviewableId, openHistory, onReviewChange} = ReviewBadgeContext.useContext();
  assert(reviewableId != null, 'ReviewLifecycleSummaryPanel requires a reviewableId');
  const activeEditorLock = useSelector(
    (state: BenchlingState) => state.ui.documentEditorLockState[reviewableId] ?? null
  );

  const panel = useReviewSummaryPanel(reviewableId);

  if (panel.state === 'PENDING') {
    return <CenteredHorizontalThrobber />;
  }
  if (panel.state === 'HIDDEN') {
    return null;
  }

  const {
    reviewable,
    review,
    authors,
    status,
    reviewChanges,
    reviewers,
    reviewLifecycle,
    reviewProcessName,
    lifecycleType,
    reviewSenderId,
    snapshot,
    acceptanceSnapshot,
    isSelfReview,
    maybeAuthorAction,
    missingTeamReviewerStages,
    actionsEnabled,
    isViewingActiveCycle,
    refetch,
  } = panel;

  const {templateId, templateCollectionId} = getTemplateIdsForReviewAnalytics(reviewable);

  // Once accepted, the acceptance snapshot supersedes the send snapshot as the relevant artifact to show.
  const currentSnapshot = acceptanceSnapshot ?? snapshot;
  const currentSnapshotSection = currentSnapshot && (
    <SnapshotSection
      snapshot={currentSnapshot}
      reviewableId={reviewableId}
      reviewId={review.id}
      onReviewChange={onReviewChange}
      canRetry={isViewingActiveCycle}
      refetch={refetch}
    />
  );

  // The live `reviewers` collection only describes the active cycle, so a historical (previously
  // published) version reconstructs its read-only stage rows from the scoped review changes instead.
  let reviewStageSteps: Array<StepObject | null> = isViewingActiveCycle
    ? getReviewStages({
        reviewers,
        canReassign:
          actionsEnabled && Boolean(permissions.canReassignRlcReviewer) && status !== ReviewStatus.REJECTED,
        canAccept: actionsEnabled && Boolean(permissions.canAccept),
        canReject: actionsEnabled && Boolean(permissions.canReject),
        lifecycleType,
        status,
        authors,
        reviewChanges,
        reviewSenderId,
        reviewableId,
      })
    : getHistoricalReviewStageSteps({
        historicalStages: getHistoricalReviewStages(reviewChanges),
        reviewableId,
        reviewSenderId,
      });
  if (isSelfReview) {
    reviewStageSteps = withSelfReviewSnapshotSection(reviewStageSteps, currentSnapshotSection);
  }

  const reviewActionToPermission = {
    [ReviewAction.RETRACT]: Boolean(permissions.canRetract),
    [ReviewAction.SEND_FOR_REVIEW]: Boolean(permissions.canSendForReview),
  };

  return (
    <div {...getTestProps({component: 'ReviewLifecycleSummaryPanel', element: 'root'})}>
      {missingTeamReviewerStages.length > 0 && <MissingReviewersWarning stages={missingTeamReviewerStages} />}
      <div className={css(styles.topBox)}>
        <div className={css(styles.summary)}>
          <span>Summary</span>
          {openHistory && (
            <Tooltip text="See full history">
              <Button
                aria-label="See full history"
                size={ButtonSize.SMALL}
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                icon="clipboard-list"
                onClick={openHistory}
              />
            </Tooltip>
          )}
        </div>
        {/* User may not have permission to perform action or project may not be configured to allow an
        RLC related author action anymore. */}
        {maybeAuthorAction &&
          actionsEnabled &&
          reviewLifecycle != null &&
          reviewActionToPermission[maybeAuthorAction.primaryReviewAction] &&
          !(
            maybeAuthorAction.primaryReviewAction === ReviewAction.SEND_FOR_REVIEW &&
            activeEditorLock &&
            DocumentEditorLockUtil.isLockedInRealtime(activeEditorLock)
          ) && (
            <PrimaryReviewAction
              primaryReviewAction={maybeAuthorAction.primaryReviewAction}
              userFacingMessage={maybeAuthorAction.userFacingMessage}
              existingReviewerUsersByStage={reviewers.reduce(
                (acc: EntityByStageId, reviewer: ReviewerGraphQL) => {
                  acc[reviewer.reviewStage.id] = reviewer.user;
                  return acc;
                },
                {}
              )}
              sourceId={reviewable.reviewSource?.id ?? null}
              activeReviewLifecycleId={reviewLifecycle.id}
              templateId={templateId}
              templateCollectionId={templateCollectionId}
            />
          )}
        {status !== ReviewStatus.RETRACTED &&
          status !== ReviewStatus.REVIEW_SNAPSHOT_IN_PROGRESS &&
          reviewProcessName && (
            <div
              {...getTestProps({component: 'ReviewLifecycleSummaryPanel', element: 'reviewProcessInfo'})}
              className={css(styles.reviewProcessInfo)}
            >
              Review process: {reviewProcessName}
            </div>
          )}
      </div>
      <div className={css(styles.panel)}>
        <StepperV2
          hideVerticalRule={lifecycleType === ReviewLifecycleType.PARALLEL}
          steps={[
            ...(isSelfReview
              ? []
              : reviewChangesIncludeSendForReview(reviewChanges)
                ? [
                    {
                      title: (
                        <div
                          {...getTestProps({component: 'ReviewStageTitle', element: 'root'})}
                          className={css(styles.title)}
                        >
                          Sent for review
                        </div>
                      ),
                      content: (
                        <div className={css(styles.stepContent)}>
                          <SentForReviewSummaryItem
                            reviewChanges={reviewChanges}
                            reviewSenderId={reviewSenderId}
                          />
                          {currentSnapshotSection}
                          {lifecycleType === ReviewLifecycleType.PARALLEL && (
                            <div className={css(styles.horizontal)} />
                          )}
                        </div>
                      ),
                      renderNumber: (
                        <ReviewerStatusIcon
                          reviewerStatus={ReviewerStatus.FINISHED}
                          isUserValidReviewer={true}
                          stageId="sent_for_review"
                        />
                      ),
                      key: '',
                    },
                  ]
                : []),
            ...reviewStageSteps,
          ]}
        />
      </div>
    </div>
  );
}

const reviewActionStyle = {
  ...FONT_PROXIMA_12,
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  margin: '8px 0px',
  color: Color.BLUE_500,
} as CSSProperties;

const styles = StyleSheet.create({
  title: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  iconContainer: {
    height: 16,
  },
  stepContent: {
    marginBottom: 20,
    width: 214,
  },
  completedIcon: {
    color: Color.GREEN_600,
    width: 16,
    height: 16,
  },
  lockIcon: {
    color: Color.GRAY_500,
    width: 16,
    height: 16,
  },
  rejectedIcon: {
    color: Color.RED_500,
    width: 16,
    height: 16,
  },
  reassignIcon: {
    color: WHITE,
    width: 10,
    height: 10,
    display: 'block',
    margin: 'auto',
  },
  reassignIconContainer: {
    backgroundColor: Color.RED_500,
    width: 16,
    height: 16,
    borderRadius: 8,
    alignContent: 'center',
  },
  pendingIcon: {
    border: `1px solid ${Color.GRAY_500}`,
    borderRadius: 8,
    height: 16,
    width: 16,
  },
  reviewStagesContainer: {
    width: '100%',
  },
  arrow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '4px 8px 4px 14px',
  },
  parallel: {
    margin: '0px 0px',
    paddingBottom: '12px',
  },
  horizontal: {
    margin: '12px 0px',
    border: `0.5px solid ${GRAY}`,
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    margin: '0px 0px',
  },
  summary: {
    ...FONT_PROXIMA_18,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  topBox: {
    display: 'flex',
    flexDirection: 'column',
    margin: '8px 0px',
  },
  reviewAction: {
    ...reviewActionStyle,
  },
  disabledReviewAction: {
    ...reviewActionStyle,
    color: Color.GRAY_500,
  },
  reviewIcon: {
    marginRight: '4px',
    width: '12px',
    height: '12px',
  },
  reviewActionItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: '16px',
  },
  snapshotSection: {
    marginTop: '8px',
  },
  reviewProcessInfo: {
    ...FONT_PROXIMA_12,
    marginTop: '4px',
    color: Color.GRAY_700,
  },
});
